import { type Measures } from "../../../types/entities";
import type {
  BoxListItem,
  CreateProductInput,
} from "../../../api/WarehouseApi";
import { BOX_ID_PATTERN } from "../../../domain/entity-ids";
import {
  isValidPhotoUrl,
  normalizeProductPhotoState,
} from "../../../domain/product-photos";
import {
  isExpiryBeforeManufacturing,
  parseInputDate,
  parseQuantityInput,
} from "../../../domain/product-form";
import { resolveBoxReferenceInput } from "../../../utils/boxReference";
export { BOX_ID_PATTERN };
export { parseInputDate, parseQuantityInput };

export interface AddProductFormState {
  name: string;
  description: string;
  photo: string;
  photos: string[];
  typeTags: string[];
  typeTagInput: string;
  parentId: string;
  manufacturingDate: string;
  expiryDate: string;
  measure: Measures;
  quantityInput: string;
}

export type AddProductDraft = AddProductFormState;

export function applyAddProductDraft(
  target: AddProductFormState,
  draft: AddProductDraft,
): void {
  const photoState = normalizeProductPhotoState({
    photo: draft.photo,
    photos: draft.photos,
  });
  target.name = draft.name;
  target.description = draft.description;
  target.photo = photoState.photo;
  target.photos = photoState.photos;
  target.typeTags = [...draft.typeTags];
  target.typeTagInput = draft.typeTagInput;
  target.parentId = draft.parentId;
  target.manufacturingDate = draft.manufacturingDate;
  target.expiryDate = draft.expiryDate;
  target.measure = draft.measure;
  target.quantityInput = draft.quantityInput;
}

export function buildDraftSnapshot(
  state: AddProductFormState,
): AddProductDraft {
  const photoState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
  });

  return {
    name: state.name,
    description: state.description,
    photo: photoState.photo,
    photos: photoState.photos,
    typeTags: [...state.typeTags],
    typeTagInput: state.typeTagInput,
    parentId: state.parentId,
    manufacturingDate: state.manufacturingDate,
    expiryDate: state.expiryDate,
    measure: state.measure,
    quantityInput: state.quantityInput,
  };
}

export function resolveParentId(
  parentIdInput: string,
  availableBoxes: BoxListItem[],
): string {
  return resolveBoxReferenceInput(parentIdInput, availableBoxes);
}

export function buildCreateProductPayload(
  state: AddProductFormState,
  availableBoxes: BoxListItem[],
): CreateProductInput {
  const parentId = resolveParentId(state.parentId, availableBoxes);
  const photoState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
  });
  const manufacturingDate = parseInputDate(state.manufacturingDate);
  const expiryDate = parseInputDate(state.expiryDate);
  const quantity = parseQuantityInput(state.quantityInput) || 1;

  return {
    name: state.name.trim(),
    description: state.description.trim(),
    photo: photoState.photo || undefined,
    photos: photoState.photos.length > 0 ? photoState.photos : undefined,
    typeTags: state.typeTags,
    measure: state.measure,
    quantity,
    parentId,
    manufacturingDate,
    expiryDate,
  };
}

export function validateAddProductForm(
  state: AddProductFormState,
  availableBoxes: BoxListItem[],
): string | null {
  if (!state.name.trim()) {
    return "Укажите название продукта";
  }

  if (state.typeTags.length === 0) {
    return "Добавьте хотя бы один тег типа";
  }

  const parentId = resolveParentId(state.parentId, availableBoxes);
  if (!parentId) {
    return "Выберите коробку для товара";
  }
  if (!BOX_ID_PATTERN.test(parentId)) {
    return "Выберите существующую коробку из списка";
  }
  if (availableBoxes.length === 0) {
    return "Сначала создайте коробку для размещения товара";
  }
  if (!availableBoxes.some((box) => box.id === parentId)) {
    return "Выберите существующую коробку из списка";
  }

  const photoState = normalizeProductPhotoState({
    photo: state.photo,
    photos: state.photos,
  });
  const invalidPhoto = photoState.photos.find(
    (photo) => !isValidPhotoUrl(photo),
  );
  if (invalidPhoto) {
    return "Каждое фото должно быть корректным URL";
  }

  if (!parseQuantityInput(state.quantityInput)) {
    return "Количество должно быть числом больше 0";
  }

  if (
    state.manufacturingDate.trim() &&
    !parseInputDate(state.manufacturingDate)
  ) {
    return "Некорректная дата производства";
  }

  if (state.expiryDate.trim() && !parseInputDate(state.expiryDate)) {
    return "Некорректный срок годности";
  }

  const manufacturingDate = parseInputDate(state.manufacturingDate);
  const expiryDate = parseInputDate(state.expiryDate);
  if (isExpiryBeforeManufacturing(manufacturingDate, expiryDate)) {
    return "Срок годности не может быть раньше даты производства";
  }

  return null;
}
